import React, { useMemo, useState } from 'react';
import {
  Badge,
  Box,
  IconButton,
  Menu,
  MenuItem,
  Skeleton,
  Stack,
  Typography,
} from '@mui/material';
import { OutlinedIcons } from '@marageti/z4-lib';
import { AgentWithUnreadCount } from '@marageti/z4-sdk/lib/travel';
import { RoleName, UserStatus } from '@marageti/z4-sdk/lib/auth';

const { OutlinedChevronDownIcon } = OutlinedIcons;

export type InboxAgentSelectorProps = {
  loggedInAgentId: string | undefined;
  isAgentAdmin: boolean;
  partnerAgentsWithUnread: AgentWithUnreadCount[] | undefined;
  selectedAgentPersonId: string | null;
  onAgentSelect: (agent: AgentWithUnreadCount | null) => void;
};

const InboxAgentSelector = ({
  loggedInAgentId,
  isAgentAdmin,
  partnerAgentsWithUnread,
  selectedAgentPersonId,
  onAgentSelect,
}: InboxAgentSelectorProps) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const isMenuOpen = Boolean(anchorEl);

  const selectedAgent = useMemo(
    () => partnerAgentsWithUnread?.find((a) => a.id === selectedAgentPersonId) ?? null,
    [partnerAgentsWithUnread, selectedAgentPersonId],
  );

  const groupedAgents = useMemo(() => {
    if (!partnerAgentsWithUnread) return { active: [], disabled: [] };

    const active: AgentWithUnreadCount[] = [];
    const disabled: AgentWithUnreadCount[] = [];

    partnerAgentsWithUnread.forEach((agent) => {
      if (agent.userInfo[0]?.roles?.includes(RoleName.AGENT_SUPPORT)) return;
      if (agent.userInfo[0]?.status === UserStatus.ENABLED) {
        active.push(agent);
      } else if (agent.userInfo[0]?.status === UserStatus.DISABLED) {
        disabled.push(agent);
      }
    });

    active.sort((a, b) => a.fullName.localeCompare(b.fullName));
    disabled.sort((a, b) => a.fullName.localeCompare(b.fullName));
    return { active, disabled };
  }, [partnerAgentsWithUnread]);

  const myInboxUnreadCount = useMemo(
    () => partnerAgentsWithUnread?.find((a) => a.id === loggedInAgentId)?.unreadConversationCount ?? 0,
    [partnerAgentsWithUnread, loggedInAgentId],
  );

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleAgentClick = (agent: AgentWithUnreadCount | null) => {
    onAgentSelect(agent);
    handleMenuClose();
  };

  return (
    <Stack direction="row" alignItems="center" sx={{ mb: 1.5 }}>
      {!partnerAgentsWithUnread ? (
        <Skeleton variant="text" width={120} height={32} sx={{ flexShrink: 0 }} />
      ) : (
        <Typography variant="h3" noWrap sx={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>
          {selectedAgent ? selectedAgent.fullName : 'My Inbox'}
        </Typography>
      )}
      {isAgentAdmin && (
        <>
          <IconButton
            sx={{ flexShrink: 0, ml: 0.5 }}
            aria-label="Select agent inbox"
            aria-controls={isMenuOpen ? 'agent-menu' : undefined}
            aria-haspopup="true"
            aria-expanded={isMenuOpen ? 'true' : undefined}
            onClick={handleMenuOpen}
          >
            <OutlinedChevronDownIcon color="primary" />
          </IconButton>
          <Menu
            id="agent-menu"
            anchorEl={anchorEl}
            open={isMenuOpen}
            onClose={handleMenuClose}
            MenuListProps={{
              'aria-labelledby': 'agent-menu-button',
              sx: { overflow: 'hidden' },
            }}
            slotProps={{
              paper: {
                sx: {
                  width: 320,
                  maxWidth: '90vw',
                  maxHeight: 400,
                },
              },
            }}
          >
            <MenuItem
              onClick={() => handleAgentClick(null)}
              sx={{
                display: 'flex',
                alignItems: 'center',
                width: '100%',
                minWidth: 0,
                overflow: 'hidden',
              }}
            >
              <Box
                sx={{
                  flex: 1,
                  minWidth: 0,
                  overflow: 'hidden',
                  mr: 2,
                }}
              >
                <Typography variant="body1" noWrap sx={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>
                  My Inbox
                </Typography>
              </Box>
              <Badge
                badgeContent={myInboxUnreadCount === 0 ? '0' : myInboxUnreadCount}
                sx={{
                  flexShrink: 0,
                  '& .MuiBadge-badge': {
                    backgroundColor: myInboxUnreadCount > 0 ? '#2196F3' : 'action.disabled',
                    color: '#fff',
                  },
                }}
              />
            </MenuItem>

            {groupedAgents.active.length > 0 && (
              <>
                <MenuItem disabled sx={{ opacity: '1 !important', justifyContent: 'center' }}>
                  <Typography align="center">--- Active ---</Typography>
                </MenuItem>
                {groupedAgents.active.map((agent) => (
                  <MenuItem
                    key={agent.id}
                    onClick={() => handleAgentClick(agent)}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      width: '100%',
                      minWidth: 0,
                      overflow: 'hidden',
                    }}
                  >
                    <Box
                      sx={{
                        flex: 1,
                        minWidth: 0,
                        overflow: 'hidden',
                        mr: 2,
                      }}
                    >
                      <Typography variant="body1" noWrap sx={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>
                        {agent.fullName}
                      </Typography>
                    </Box>
                    <Badge
                      badgeContent={agent.unreadConversationCount === 0 ? '0' : agent.unreadConversationCount}
                      sx={{
                        flexShrink: 0,
                        '& .MuiBadge-badge': {
                          backgroundColor:
                            agent.unreadConversationCount > 0 ? '#2196F3' : 'action.disabled',
                          color: '#fff',
                        },
                      }}
                    />
                  </MenuItem>
                ))}
              </>
            )}

            {groupedAgents.disabled.length > 0 && (
              <>
                <MenuItem disabled sx={{ opacity: '1 !important', justifyContent: 'center' }}>
                  <Typography align="center">--- Disabled ---</Typography>
                </MenuItem>
                {groupedAgents.disabled.map((agent) => (
                  <MenuItem
                    key={agent.id}
                    onClick={() => handleAgentClick(agent)}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      width: '100%',
                      minWidth: 0,
                      overflow: 'hidden',
                    }}
                  >
                    <Box
                      sx={{
                        flex: 1,
                        minWidth: 0,
                        overflow: 'hidden',
                        mr: 2,
                      }}
                    >
                      <Typography variant="body1" noWrap sx={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>
                        {agent.fullName}
                      </Typography>
                    </Box>
                    <Badge
                      badgeContent={agent.unreadConversationCount === 0 ? '0' : agent.unreadConversationCount}
                      sx={{
                        flexShrink: 0,
                        '& .MuiBadge-badge': {
                          backgroundColor:
                            agent.unreadConversationCount > 0 ? '#2196F3' : 'action.disabled',
                          color: '#fff',
                        },
                      }}
                    />
                  </MenuItem>
                ))}
              </>
            )}
          </Menu>
        </>
      )}
    </Stack>
  );
};

export default InboxAgentSelector;
